import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  IFilter,
  IPopulate,
  IQueryParam,
  IRecorridoDinamico,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import * as turf from '@turf/turf';
import { Router, ActivatedRoute } from '@angular/router';
import { RecorridosDinamicosService } from '../../../../../auxiliares/servicios/http/recorridos-dinamicos.service';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../../auxiliares/tabla/tabla.component';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../../auxiliares/tabla/filtro/filtro.component';
import { fechaCelda } from '../../../../../auxiliares/tabla/celdas';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import {
  LineStringMapa,
  MapaFacilData,
} from '../../../../../auxiliares/mapa-facil/interfaces';
import { MapaFacilDialogComponent } from '../../../../../auxiliares/mapa-facil-dialog/mapa-facil-dialog.component';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import { ParamsService } from '../../../../../auxiliares/servicios/params.service';
import {
  DetalleCumplimientoPuntosComponent,
  DetalleCumplimientoPuntosData,
} from '../../recorridos/detalle-cumplimiento-puntos/detalle-cumplimiento-puntos.component';
import { ClienteJerarquiaService } from '../../../../../auxiliares/servicios/cliente-jerarquia.service';

@Component({
  selector: 'app-listado-recorridos-dinamicos-vehiculos',
  templateUrl: './listado-recorridos-dinamicos.component.html',
  styleUrl: './listado-recorridos-dinamicos.component.scss',
  standalone: false,
})
export class ListadoRecorridosDinamicosVehiculosComponent
  implements OnInit, OnDestroy
{
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  private dialogService = inject(DialogService);
  private service = inject(RecorridosDinamicosService);
  private navigator = inject(Router);
  private route = inject(ActivatedRoute);
  private formularios = inject(FormularioDialogService);
  private paramsService = inject(ParamsService);

  public name = 'ListadoRecorridosDinamicosVehiculosComponent';
  public recorridos?: IRecorridoDinamico[];
  public totalCount = 0;
  public columnas?: IColumnas<IRecorridoDinamico>[];

  public recorridos$?: Subscription;

  public botonCrear: ICrearBoton = {
    mostrar: this.helper.tienePermiso('Vehículos - Crear recorridos'),
    tooltip: 'Crear recorrido dinámico',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.irAPlanificador(),
  };
  public search: IRegExpSearch = {
    fields: ['nombre'],
  };
  public cliente: IFiltroTabla = {
    filter: {
      field: 'idCliente',
    },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la entidad: el cliente elegido y toda su
  // descendencia.
  public ancestro: IFiltroTabla = {
    filter: {
      field: 'idsAncestros',
      incluirPropio: true,
    },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  // "Vigente"/"Finalizado" no son un campo propio: se resuelven comparando
  // ventana.fechaHasta contra el momento en que se abrió el filtro (mismo
  // criterio que estaFinalizado()).
  public estado: IFiltroTabla = {
    filter: { field: 'ventana.fechaHasta' },
    label: 'Estado',
    tipo: 'select',
    rapido: true,
    elementos: [
      { label: 'Vigente', value: { $gt: new Date().toISOString() } },
      { label: 'Finalizado', value: { $lte: new Date().toISOString() } },
    ],
    selectLabel: 'label',
    selectValue: 'value',
    compararPorJson: true,
  };
  public filtros: IFiltroTabla[] = [];
  public filter: IFilter<IRecorridoDinamico> = {};
  public populate: IPopulate[] = [
    {
      path: 'activo',
      select: 'identificacion',
    },
    {
      path: 'cliente',
      select: 'nombre',
    },
  ];
  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    populate: JSON.stringify(this.populate),
    sort: '-_id',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
    childrenLevel: 0,
  };

  public async listar(): Promise<void> {
    this.recorridos$?.unsubscribe();
    this.recorridos$ = this.lista2
      .recorridosDinamicos(this.query, 30)
      .subscribe((data) => {
        this.totalCount = data.totalCount;
        this.recorridos = data.datos;
      });
  }

  /** La ventana ya terminó — el recorrido quedó "Finalizado" y no se puede editar. */
  public estaFinalizado(recorrido: IRecorridoDinamico): boolean {
    const fechaHasta = recorrido.ventana?.fechaHasta;
    return !!fechaHasta && fechaHasta <= new Date().toISOString();
  }

  public irAPlanificador() {
    this.paramsService.setParams({ recorridoDinamico: null });
    this.navigator.navigate(['planificador'], { relativeTo: this.route });
  }

  public editar(recorrido: IRecorridoDinamico) {
    if (this.estaFinalizado(recorrido)) {
      this.helper.notifError('El recorrido ya terminó, no se puede editar.');
      return;
    }
    this.paramsService.setParams({ recorridoDinamico: recorrido });
    this.navigator.navigate(['planificador'], { relativeTo: this.route });
  }

  public async ubicar(r: IRecorridoDinamico) {
    const coords = r.geojson?.coordinates;
    if (!coords || coords.length === 0) {
      this.helper.notifError('El recorrido no tiene coordenadas');
      return;
    }

    const polyline: LineStringMapa = {
      color: r.color,
      points: coords,
      width: 6,
      centrar: true,
    };
    // Círculo con el radio real de tolerancia por parada, mismo criterio
    // que el mapa embebido de detalle de vehículo.
    const polygons =
      r.paradas
        ?.filter((p) => p.geojson?.coordinates)
        .map((parada, i) => {
          const circulo = turf.circle(
            parada.geojson!.coordinates as [number, number],
            parada.radioMetros ?? 60,
            { steps: 64, units: 'meters' },
          );
          return {
            points: circulo.geometry.coordinates as [number, number][][],
            color: r.color || '#1976d2',
            fillColor: r.color || '#1976d2',
            width: 2,
            label: `${i + 1}`,
          };
        }) ?? [];
    const data: MapaFacilData = {
      polylines: [polyline],
      polygons,
      vista: 'mapa',
      zoom: 14,
    };
    this.formularios.abrir(MapaFacilDialogComponent, {
      tamano: 'grande',
      data,
      cerrarAfuera: true,
    });
  }

  public verCumplimiento(recorrido: IRecorridoDinamico) {
    this.formularios.abrir<
      DetalleCumplimientoPuntosComponent,
      DetalleCumplimientoPuntosData
    >(DetalleCumplimientoPuntosComponent, {
      tamano: 'mediano',
      data: { recorrido },
      cerrarAfuera: true,
    });
  }

  public async eliminar(recorrido: IRecorridoDinamico) {
    const res = await this.dialogService.confirm(
      'Eliminar recorrido dinámico',
      `¿Querés eliminar el recorrido ${recorrido.nombre}?`,
    );
    if (!res) return;
    try {
      await this.service.delete(recorrido._id!);
      this.helper.notifSuccess('Recorrido eliminado');
      await this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  private setColumnas() {
    this.columnas = [
      {
        header: { label: 'Nombre' },
        row: { field: 'nombre', noData: 'Sin nombre' },
      },
      {
        header: { label: 'Vehículo' },
        row: {
          parse(dato) {
            return dato.activo?.identificacion;
          },
          noData: 'Sin vehículo',
        },
      },
      {
        header: { label: 'Ventana' },
        row: {
          parse(dato) {
            if (!dato.ventana?.fechaDesde) return undefined;
            const desde = fechaCelda(dato.ventana.fechaDesde);
            const hasta = dato.ventana.fechaHasta
              ? fechaCelda(dato.ventana.fechaHasta)
              : '';
            return `${desde} → ${hasta}`;
          },
          noData: 'Sin ventana',
        },
      },
      {
        header: { label: 'Estado' },
        row: {
          estado: (dato) =>
            this.estaFinalizado(dato)
              ? { texto: 'Finalizado', tono: 'neutro', icono: 'task_alt' }
              : { texto: 'Vigente', tono: 'ok', icono: 'toggle_on' },
        },
      },
      {
        header: { label: 'Cumplió' },
        row: {
          // Puntos visitados sobre el total, no solo sí/no: con 15 puntos y
          // freno en el 13, "13/15" dice mucho más que un "No" pelado.
          parse(dato) {
            const puntos = dato.detalleCumplimiento?.puntos;
            if (!puntos?.length) return undefined;
            const visitados = puntos.filter((p) => p.visitado).length;
            return `${visitados}/${puntos.length}`;
          },
          noData: 'Sin puntos',
        },
      },
    ];
    this.filtros.push(this.estado);
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.push(this.cliente);
      this.filtros.push(this.ancestro);
      this.columnas.push({
        header: { label: 'Cliente' },
        row: {
          html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
          tooltip: (dato) =>
            this.clienteJerarquia.tooltipJerarquia(dato.cliente),
          noData: 'Sin cliente',
        },
      });
    }
    this.columnas.push({
      header: { label: 'Acciones' },
      row: {
        acciones: [
          {
            tipo: 'material',
            color: 'primary',
            icon: 'map',
            tooltip: 'Ver en el mapa',
            click: (dato) => this.ubicar(dato),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'fact_check',
            tooltip: 'Ver cumplimiento',
            click: (dato) => this.verCumplimiento(dato),
          },
          {
            tipo: 'material',
            color: 'primary',
            icon: 'edit',
            tooltip: 'Editar',
            click: (dato) => this.editar(dato),
            disabled: (dato) =>
              this.estaFinalizado(dato) ||
              !this.helper.tienePermiso('Vehículos - Editar recorridos'),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (dato) => this.eliminar(dato),
            disabled: () =>
              !this.helper.tienePermiso('Vehículos - Eliminar recorridos'),
          },
        ],
      },
    });
  }

  async ngOnInit() {
    this.setColumnas();
    await this.listar();
  }

  ngOnDestroy() {
    this.recorridos$?.unsubscribe();
  }
}
